import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';
import { useProjectSettingsStore } from '@/store/projectSettingsStore';
import type { ShotMetadataOption } from './shotSettings';

/** The project-scoped settings the web modal owns, mirroring the native
 *  ProjectSettingsPopupPanel's live rows. Every string is an ENUM MEMBER NAME
 *  (Unity's `.ToString()`), never a localized label — labels arrive in `options`
 *  pre-formatted and pre-localized by Unity, so the web never formats a number.
 *
 *  The numbering catalogs' labels are WORKED EXAMPLES in a fixed shape, and
 *  `numberingExample.ts` PARSES them rather than restating Unity's numbering rules:
 *  the two revision catalogs send three comma-separated cells — `"1, 1A, 2"` /
 *  `"1, A1, 2"` for scenes, `"A, A-1, B"` / `"A, 1-A, B"` for shots (the digit forms
 *  `"1, 1-A, 2"` / `"1, A-1, 2"` when Use Digits is on) — and the separator catalog
 *  sends `"1" + separator + "A"`, i.e. `"1A"` / `"1-A"` / `"1.A"`. Built by
 *  BuildSceneNumberingOptions / BuildShotNumberingOptions /
 *  BuildSceneShotSeparatorOptions (TimelineWebViewPanel.cs); change a shape there and
 *  the worked-example card degrades to showing the label whole.
 *
 *  The last four fields are the BATCHED half of the Watermark & Logo subpage. Its other
 *  controls — the image itself, and the two enable switches — are absent from this
 *  interface for the same reason Lock Numbering and Renumber Project are: they are not
 *  batched fields at all but their own immediate verbs (see the senders at the bottom),
 *  because natively they write the database the moment they are used. What can and
 *  cannot ride a Done is the whole distinction between `values` and `flags` below. */
export interface ProjectSettingsValues {
  projectName: string;
  aspectRatioType: string;        // e.g. "AspectRatio16to9"
  sensorSizeType: string;         // e.g. "SensorSize_35mm"
  postProcessingKey: string;      // sanitized PP name key; "" allowed
  /** Per-project tone mapping (fileVersion 56, `document.tonemappingmode`). An ENUM MEMBER NAME of
   *  Unity's `ToneMappingPreferenceManager.ToneMappingMode` — "None" | "Neutral" | "Aces" | "AgX" |
   *  "PbrNeutral" | "BlenderAgX" — never the integer and never a localized label (those arrive in
   *  `options.toneMappingModes`). Unity substitutes its default (None) for a project whose stored
   *  value is still the unset sentinel, so this is never "" on the way out of Unity. */
  toneMappingMode: string;
  showCameraEquipment: boolean;
  showLightingEquipment: boolean;
  frameRateType: string;          // e.g. "FrameRate30_00"
  sceneNumberingType: string;     // "NumberLetter" | "LetterNumber"
  shotNumberingType: string;      // "NumberLetter" | "LetterNumber"
  sceneShotSeparatorType: string; // "Nothing" | "Hyphen" | "Period"
  useStartingSceneNumber: boolean;
  startingSceneNumber: number;
  useFullAlphabetForShotNumbering: boolean;
  useDigitsForShotNumber: boolean;
  /** 0–1 opacity of the export watermark overlay. */
  watermarkAlpha: number;
  /** Stand in for the missing overlay with the other one (one flag, two mirrored controls). */
  useOtherImageIfNeeded: boolean;
  useCustomExportText: boolean;
  customExportText: string;
}

export interface ProjectSettingsOptions {
  aspectRatios: ShotMetadataOption[];
  sensorSizes: ShotMetadataOption[];
  postProcessing: ShotMetadataOption[];
  /** The six tone-mapping modes, built from Unity's `ToneMappingPreferenceManager.AllModes` with
   *  labels from ITS localization terms (there is no web copy for the mode names, only for the row
   *  label). `value` is the enum member name that rides `values.toneMappingMode`. */
  toneMappingModes: ShotMetadataOption[];
  frameRates: ShotMetadataOption[];
  sceneNumbering: ShotMetadataOption[];
  shotNumbering: ShotMetadataOption[];
  sceneShotSeparator: ShotMetadataOption[];
}

/** Echo-only state: everything Unity writes through an IMMEDIATE verb of its own and passes through
 *  untouched on apply. Nothing here may ever join `values` — a Done assembled from an echo that
 *  predates one of those writes would then revert it. */
export interface ProjectSettingsFlags {
  /** Numbering is locked project-wide (native Lock Numbering). Written by
   *  `sendLockProjectNumbering`; a fresh echo of this flag is how that write reports back. */
  numberingLocked: boolean;
  /** A watermark image is stored on the project. Reported WITHOUT the cross-use fallback, so it is
   *  the raw fact the subpage draws its import-tile-vs-preview decision from. */
  hasWatermarkImage: boolean;
  /** The stored watermark is switched on for exports (`sendSetUseWatermarkImage`). */
  useWatermarkImage: boolean;
  hasLogoImage: boolean;
  useLogoImage: boolean;
}

export interface ProjectSettingsPayloadData {
  values: ProjectSettingsValues;
  options: ProjectSettingsOptions;
  flags: ProjectSettingsFlags;
  /** Set when Unity refuses something. `field` names the control to blame — `startingSceneNumber`
   *  for the one validated apply field, `numberingLocked` / `renumberProject` for the two numbering
   *  verbs, `watermarkImage` / `logoImage` for the image ones. `messageKey` is a `common` i18n key
   *  the modal renders directly. */
  error: { field: string; messageKey: string } | null;
}

export interface SetProjectSettingsMessage {
  type: 'setProjectSettings';
  payload: ProjectSettingsPayloadData;
}

/** The stored watermark/logo images as data URLs, or null where no image exists. Sent unprompted
 *  after every image add/delete and on demand when the subpage opens. Kept OFF the settings echo on
 *  purpose: that echo is re-sent on every apply, lock and renumber, and none of those change a
 *  pixel — riding along would put hundreds of KB of base64 on each. */
export interface SetWatermarkLogoPreviewsMessage {
  type: 'setWatermarkLogoPreviews';
  payload: { watermarkImageUrl: string | null; logoImageUrl: string | null };
}

/** A rendered frame of Unity's bundled mini scene, framed and graded with the four picks the modal
 *  is currently showing — the live version of what used to be a CSS box with the labels printed in
 *  it. Unity restates the four inputs it was ASKED for (not the enums it parsed them into), so the
 *  reply always fills exactly the cache key the request was built from; see the Project Settings
 *  Preview region in TimelineWebViewPanel.cs for why that direction is the right one here. */
export interface ProjectSettingsPreviewPayloadData {
  aspectRatioType: string;
  sensorSizeType: string;
  postProcessingKey: string;
  /** Rendered UNDER this mode without persisting it (Unity applies it around the one render and puts
   *  the effective mode back), so the box previews a candidate the user has not pressed Done on. */
  toneMappingMode: string;
  imageUrl: string;
}
export interface SetProjectSettingsPreviewMessage {
  type: 'setProjectSettingsPreview';
  payload: ProjectSettingsPreviewPayloadData;
}

/** Cache key for projectSettingsStore.previews. Every input Unity rendered from, in a fixed order —
 *  change one of them and the modal asks for a fresh render instead of showing a stale frame. The
 *  sensor belongs here as much as the aspect ratio does: it changes the FRAMING (effective sensor
 *  width at a fixed 50 mm lens → field of view), which is exactly what the old CSS box could not show.
 *  The tone mapping is the fourth input for the same reason the post-processing key is the third: it
 *  is a per-project GRADE, so two picks differing only there are two different frames. */
export function projectSettingsPreviewKey(aspectRatioType: string, sensorSizeType: string, postProcessingKey: string, toneMappingMode: string): string {
  return `${aspectRatioType}|${sensorSizeType}|${postProcessingKey}|${toneMappingMode}`;
}

export type ProjectSettingsMessage =
  | SetProjectSettingsMessage
  | SetWatermarkLogoPreviewsMessage
  | SetProjectSettingsPreviewMessage;

export const projectSettingsHandlers = {
  setProjectSettings: (msg: SetProjectSettingsMessage) => {
    // `values` is the shape gate (there is no id field to key on, unlike scene/shot settings).
    if (msg.payload && msg.payload.values) {
      useProjectSettingsStore.getState().setCurrent(msg.payload);
    }
  },
  setWatermarkLogoPreviews: (msg: SetWatermarkLogoPreviewsMessage) => {
    if (!msg.payload) return;
    // `previewsLoaded` is what separates "no image" from "not asked yet" — the tile shows a
    // placeholder for the second and the import affordance for the first, and the flags alone
    // cannot tell them apart.
    useProjectSettingsStore.getState().setPreviews(
      msg.payload.watermarkImageUrl ?? null,
      msg.payload.logoImageUrl ?? null,
    );
  },
  setProjectSettingsPreview: (msg: SetProjectSettingsPreviewMessage) => {
    if (!msg.payload || typeof msg.payload.imageUrl !== 'string') return;
    const { aspectRatioType, sensorSizeType, postProcessingKey, toneMappingMode, imageUrl } = msg.payload;
    const key = projectSettingsPreviewKey(aspectRatioType, sensorSizeType, postProcessingKey, toneMappingMode);
    useProjectSettingsStore.setState((s) => ({ previews: { ...s.previews, [key]: imageUrl } }));
  },
} satisfies HandlerMap;

/** Apply carries exactly the 19 value fields — Unity's JsonUtility DTO binds them from the
 *  TOP LEVEL of the message beside "action" (which it ignores). */
export type ApplyProjectSettingsPayload = ProjectSettingsValues;

export function sendRequestProjectSettings(): void {
  enqueueSendToNative('requestProjectSettings', {});
}

export function sendApplyProjectSettings(payload: ApplyProjectSettingsPayload): void {
  enqueueSendToNative('applyProjectSettings', { ...payload });
}

/** Ask Unity to render the preview frame for these four picks. Read-only — none of the four is
 *  persisted by this verb; they reach the project through `applyProjectSettings` and nothing else.
 *  That is worth restating for the tone mapping in particular: it is the one input here that Unity
 *  has to APPLY globally to render at all, so it applies the candidate around this single render and
 *  puts the open project's effective mode back afterwards (RunWithTemporaryMode).
 *
 *  NO DEBOUNCE, deliberately. Every control feeding this is a discrete dropdown, so one pick is one
 *  render, and the caller's "only if this key is missing" gate already collapses repeats (going back
 *  to a pick you have seen costs nothing). A trailing debounce would only be worth adding if a
 *  continuous control — a focal-length slider, say — ever fed this key. */
export function sendRequestProjectSettingsPreview(aspectRatioType: string, sensorSizeType: string, postProcessingKey: string, toneMappingMode: string): void {
  enqueueSendToNative('requestProjectSettingsPreview', { aspectRatioType, sensorSizeType, postProcessingKey, toneMappingMode });
}

/** Tell Unity the modal is gone so it can drop the mini scene it built to render those frames. Not a
 *  correctness requirement — Unity also releases it from its own panel teardown and on canvas entry —
 *  but it is what keeps the mini project's character models from sitting in memory for the rest of the
 *  session on the devices the iOS memory work targets. */
export function sendCloseProjectSettingsPreview(): void {
  enqueueSendToNative('closeProjectSettingsPreview', {});
}

/** Lock / unlock the project's scene+shot numbering. IMMEDIATE — it does not ride the batched Done
 *  payload, because natively it does not either: locking burns the current numbers into every scene
 *  and shot row the moment it is confirmed. An EXPLICIT target state, never a toggle: the switch is
 *  drawn from a `flags.numberingLocked` that may already be stale by the time it is tapped, and a
 *  toggle would then invert the wrong way (same reasoning as `sendOmitScene`). Unity no-ops when the
 *  project is already in the requested state, and answers every call — success, no-op or failure —
 *  with a fresh `setProjectSettings` echo, which is the only resolution signal there is. */
export function sendLockProjectNumbering(locked: boolean): void {
  enqueueSendToNative('lockProjectNumbering', { locked });
}

/** Renumber every scene and shot from its position in the script, re-locking afterwards if the
 *  project was locked (Unity mirrors the native panel's ordering). Also immediate, also resolved by
 *  the next `setProjectSettings` echo, and — unlike the lock — it has no observable state of its own
 *  on the web, so a failure can only ever surface through that echo's `error`. */
export function sendRenumberProject(): void {
  enqueueSendToNative('renumberProject', {});
}

// ─── Watermark & Logo ───────────────────────────────────────────────────────────────────────
// Everything below is IMMEDIATE, for the same reason the two verbs above are: natively the image
// add/delete writes the watermarkimages/logoimages row outright and the enable switches end in
// their own UPDATE, none of it gated behind that panel's Done. The consequence is inherited too —
// an image imported or deleted here survives a Cancel.

/** Which half of the subpage a verb addresses. The verb NAME carries this on the wire (there is no
 *  target field), matching the setStaticImage/applyBackground families where each feature owns its
 *  own verb; this type only keeps the two senders from being written twice. */
export type WatermarkLogoTarget = 'watermark' | 'logo';

// Same 512 KB threshold and chunk size as every other web→native image transfer
// (embeddedBridge.sendStaticImageToUnity, sceneBackground.sendSceneBackgroundToUnity,
// share.requestSocialShare). It is not a Unity limit: the drained outbox batch is delivered as a
// `previspro://` URL navigation, so one oversized message would blow past WKWebView's URL ceiling.
const CHUNK_THRESHOLD = 512 * 1024;
const CHUNK_SIZE = 512 * 1024;

/** Longest-edge cap applied BEFORE the transfer. Purely a transport bound — Unity re-clamps to its
 *  own per-performance-tier pixel budget on arrival, which is the one that decides what gets stored
 *  (and is smaller than this on every tier below Desktop). Without it a 48 MP phone photo would
 *  arrive as ~40 chunks and ~30 MB of base64 to reach a 512-px preview tile. */
export const WATERMARK_IMAGE_MAX_EDGE = 2048;

/** The only two formats `Texture2D.LoadImage` can decode on the Unity side
 *  (UnityEngine.ImageConversion). Anything else the WebView will happily hand us — and the file
 *  picker's accept list deliberately offers WebP and GIF, because the browser decodes them fine —
 *  has to be re-encoded here or it arrives as bytes Unity silently fails to load. */
const UNITY_DECODABLE_MIME_TYPES = new Set(['image/png', 'image/jpeg']);

/** Reads the MIME type out of a data URL's prefix ("data:image/webp;base64,…" → "image/webp"). */
function dataUrlMimeType(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(',');
  if (commaIndex < 0 || !dataUrl.startsWith('data:')) return '';
  return dataUrl.substring(5, commaIndex).split(';')[0].toLowerCase();
}

/**
 * Prepare `dataUrl` for the trip to Unity: **PNG**, and no longer than `maxEdge` on its longest side.
 *
 * TWO independent reasons to go through the canvas, and they are not the same reason:
 *
 *  1. **Format.** Unity decodes the received bytes with `Texture2D.LoadImage`, which handles PNG and
 *     JPEG only. A WebP or GIF — both offered by the picker's accept list, both common for logo
 *     downloads — must be re-encoded here whatever its size, or it reaches
 *     `ProcessWatermarkLogoImageBytes` as bytes `LoadImage` returns false for and the user gets an
 *     import failure with no hint that the format was the problem. Size and format used to share one
 *     branch, which made this fail INTERMITTENTLY BY FILE SIZE: the same logo.webp worked at 3000 px
 *     (re-encoded on the way past the cap) and failed at 800 px (skipped).
 *  2. **Size.** A transport bound only — Unity re-clamps to its own per-performance-tier pixel budget
 *     on arrival, which is the one that decides what gets stored. Without it a 48 MP phone photo
 *     would arrive as ~40 chunks of base64 to fill a 512-px preview tile.
 *
 * PNG, not the smaller JPEG, because these images are overlays: a watermark's transparency is the
 * point of it, and JPEG would flatten it onto black. Unity stores PNG for the same reason (both
 * native call sites pass `hasAlpha: true` unconditionally).
 *
 * **Failure is asymmetric, deliberately.** For a source Unity CAN decode, any canvas trouble (no 2D
 * context, a tainted or undecodable image, a runtime with no PNG encoder) falls back to the original
 * data URL: the transfer is chunked either way and Unity clamps either way, so the worst case is a
 * slower import, never a failed one. For a source Unity CANNOT decode there is no such fallback —
 * sending the original would be sending bytes we know will fail — so this THROWS, and the caller's
 * existing catch surfaces `projectSettingsImageReadFailed`.
 */
export async function prepareImageForTransport(dataUrl: string, maxEdge = WATERMARK_IMAGE_MAX_EDGE): Promise<string> {
  const unityCanDecode = UNITY_DECODABLE_MIME_TYPES.has(dataUrlMimeType(dataUrl));
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = dataUrl;
    });

    const longest = Math.max(image.naturalWidth, image.naturalHeight);
    // The ONLY path that skips the canvas: already a format Unity reads, already within the cap.
    if (unityCanDecode && (!longest || longest <= maxEdge)) return dataUrl;

    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const out = canvas.toDataURL('image/png');
    // A canvas with no PNG encoder answers with the 'data:,' sentinel rather than throwing.
    if (!out.startsWith('data:image/')) throw new Error('png encode unavailable');
    return out;
  } catch (err) {
    if (unityCanDecode) return dataUrl;
    throw err instanceof Error ? err : new Error('image could not be converted');
  }
}

/**
 * Ship a watermark or logo image to Unity, chunked when it exceeds the threshold.
 *
 * `fileName` rides EVERY chunk rather than only the first: Unity's reassembly binds each chunk with
 * JsonUtility independently and keeps the first non-empty name it sees, and a field absent from a
 * message binds to null there — so sending it once would make its arrival depend on chunk order.
 */
export function sendWatermarkLogoImage(target: WatermarkLogoTarget, imageDataUrl: string, fileName = ''): void {
  const commaIndex = imageDataUrl.indexOf(',');
  if (commaIndex < 0) return;
  const base64Data = imageDataUrl.substring(commaIndex + 1);
  const singleVerb = target === 'watermark' ? 'applyWatermarkImage' : 'applyLogoImage';

  if (base64Data.length <= CHUNK_THRESHOLD) {
    enqueueSendToNative(singleVerb, { imageDataUrl, fileName });
    return;
  }

  const chunkVerb = target === 'watermark' ? 'applyWatermarkImageChunk' : 'applyLogoImageChunk';
  const totalChunks = Math.ceil(base64Data.length / CHUNK_SIZE);
  const mimeType = imageDataUrl.substring(5, commaIndex).split(';')[0];
  for (let i = 0; i < totalChunks; i++) {
    enqueueSendToNative(chunkVerb, {
      mimeType, fileName, chunkIndex: i, totalChunks,
      data: base64Data.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE),
    });
  }
}

/** Delete the stored image. Unity answers with a fresh settings echo plus a fresh preview pair;
 *  there is no failure channel because the native delete returns nothing to report — the echo's
 *  `hasWatermarkImage`/`hasLogoImage` is the answer either way. */
export function sendDeleteWatermarkLogoImage(target: WatermarkLogoTarget): void {
  enqueueSendToNative(target === 'watermark' ? 'deleteWatermarkImage' : 'deleteLogoImage', {});
}

/** Switch the stored overlay on or off for exports. An EXPLICIT target state, never a toggle — same
 *  reasoning as `sendLockProjectNumbering`: the switch is drawn from a `flags` value that may be
 *  stale by the time it is tapped. Unity refuses when no image exists and echoes the truth back. */
export function sendSetUseWatermarkLogoImage(target: WatermarkLogoTarget, enabled: boolean): void {
  enqueueSendToNative(target === 'watermark' ? 'setUseWatermarkImage' : 'setUseLogoImage', { enabled });
}

/** Ask for both stored images as data URLs. Only needed on subpage entry: every mutating verb above
 *  already answers with a fresh pair of its own. */
export function sendRequestWatermarkLogoPreviews(): void {
  enqueueSendToNative('requestWatermarkLogoPreviews', {});
}
